import { Dispatch, SetStateAction, useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { DropdownWithCheckboxesItem } from "@/components/DropdownWithCheckboxes/DropdownWithCheckboxes";

import { User } from "@/pages/Public/pages/Login/schema/login";

import { IFilterWithValues } from "../../../List/components/FilterModal";
import { CustomListsAutocompleteForSequencerItem } from "../../../List/components/CustomListsAutocompleteForSequencer";
import { Tabs, TAB_PARAM, getTabFromUrl } from "./outboxTabs";
import { OutboxSource } from "./OutboxSourceToggle";
import { ALL_SENDERS_ID, allSendersItem, meSenderTitle } from "./outboxSenders";
import {
	getStatusTabFromUrl,
	loadAllInclude,
	OutboxStatusTab,
	saveAllInclude,
	STATUS_TAB_PARAM,
	toggleAllInclude,
} from "./outboxStatusTabs";

interface UseOutboxUrlStateArgs {
	user: User | null;
	/** Whether the senders-users query has resolved (any data, even an empty list). */
	sendingUsersLoaded: boolean;
	/** Whether the logged-in user is one of the sending users. */
	doesSendingUsersHaveMe: boolean | undefined;
	searchValue: string;
	setSearchValue: (value: string) => void;
}

export interface OutboxUrlState {
	currentTab: Tabs;
	setCurrentTab: Dispatch<SetStateAction<Tabs>>;
	statusTab: OutboxStatusTab;
	setStatusTab: (tab: OutboxStatusTab) => void;
	/** The All tab's configured include-set (persisted per user in localStorage). */
	allInclude: OutboxStatusTab[];
	toggleInclude: (tab: OutboxStatusTab) => void;
	selectedLists: CustomListsAutocompleteForSequencerItem[];
	setSelectedLists: Dispatch<SetStateAction<CustomListsAutocompleteForSequencerItem[]>>;
	selectedSenders: DropdownWithCheckboxesItem[];
	setSelectedSenders: Dispatch<SetStateAction<DropdownWithCheckboxesItem[]>>;
	/** Campaign uuids of the Outbox campaign filter (PREC-3850); empty = no narrowing. */
	selectedCampaignIds: string[];
	setSelectedCampaignIds: Dispatch<SetStateAction<string[]>>;
	/** Chat/campaign source scope (PREC-3850); "all" = no narrowing. */
	outboxSource: OutboxSource;
	setOutboxSource: Dispatch<SetStateAction<OutboxSource>>;
	filter: IFilterWithValues | undefined;
	setFilter: Dispatch<SetStateAction<IFilterWithValues | undefined>>;
}

const buildDefaultSenderItem = (
	user: User | null,
	doesSendingUsersHaveMe: boolean | undefined
): DropdownWithCheckboxesItem =>
	doesSendingUsersHaveMe
		? { id: user?.id ?? ALL_SENDERS_ID, title: meSenderTitle(user) }
		: allSendersItem();

/**
 * Owns every piece of Outbox state that lives in the URL (content tab, outbox view,
 * assign-status filter, list scope, senders, advanced filter, text search): seeds the
 * initial values from the query string so deep links work, and mirrors changes back
 * with history-replacing navigations so the state survives reloads and is shareable.
 */
export const useOutboxUrlState = ({
	user,
	sendingUsersLoaded,
	doesSendingUsersHaveMe,
	searchValue,
	setSearchValue,
}: UseOutboxUrlStateArgs): OutboxUrlState => {
	const location = useLocation();
	const navigate = useNavigate();

	// The query string as last written. Every effect below writes through `replaceSearch`, which
	// starts from this, not from the `location.search` captured at render: two effects that run
	// in the same commit (applyLists sets the campaigns and the lists in one step) otherwise each
	// start from the same stale string, and the later one puts back what the earlier one removed
	// (PREC-4291 QA: a dropped campaign came back on reload as "Campaign from link").
	const latestSearchRef = useRef(location.search);

	latestSearchRef.current = location.search;

	const replaceSearch = useCallback(
		(change: (params: URLSearchParams) => void) => {
			const params = new URLSearchParams(latestSearchRef.current);

			change(params);

			const search = params.toString();

			latestSearchRef.current = search ? `?${search}` : "";
			navigate({ search }, { replace: true });
		},
		[navigate]
	);

	const [currentTab, setCurrentTab] = useState<Tabs>(getTabFromUrl(location.search));

	const getSelectedListsFromUrl = (): CustomListsAutocompleteForSequencerItem[] => {
		const params = new URLSearchParams(location.search);
		const listIdsParam = params.get("listIds");

		return listIdsParam
			? (JSON.parse(listIdsParam) as CustomListsAutocompleteForSequencerItem[])
			: [];
	};

	const [selectedLists, setSelectedLists] =
		useState<CustomListsAutocompleteForSequencerItem[]>(getSelectedListsFromUrl());

	const getSendersFromUrl = (): DropdownWithCheckboxesItem[] => {
		const params = new URLSearchParams(location.search);

		return JSON.parse(
			params.get("senders") ||
				JSON.stringify([buildDefaultSenderItem(user, doesSendingUsersHaveMe)])
		) as DropdownWithCheckboxesItem[];
	};

	const [selectedSenders, setSelectedSenders] =
		useState<DropdownWithCheckboxesItem[]>(getSendersFromUrl());

	// Status tab (PREC-3850). Seeds from the URL (with legacy outboxView migration);
	// persisting deletes the retired legacy params so migrated links stay clean.
	const [statusTab, setStatusTab] = useState<OutboxStatusTab>(() =>
		getStatusTabFromUrl(location.search)
	);

	// One-shot per change, like the sibling effects (a mount-time write would be
	// clobbered by the later effects' stale query-string snapshots anyway). Legacy
	// deep links therefore keep their params until the first tab change — harmless,
	// since getStatusTabFromUrl resolves them on every load.
	useEffect(() => {
		replaceSearch((params) => {
			params.set(STATUS_TAB_PARAM, statusTab);
			params.delete("outboxView");
			params.delete("assignStatus");
		});
	}, [statusTab]);

	const [allInclude, setAllInclude] = useState<OutboxStatusTab[]>(() => loadAllInclude());

	const toggleInclude = useCallback((tab: OutboxStatusTab) => {
		setAllInclude((current) => {
			const next = toggleAllInclude(current, tab);

			saveAllInclude(next);

			return next;
		});
	}, []);

	useEffect(() => {
		replaceSearch((params) => {
			params.set(TAB_PARAM, currentTab);
		});
	}, [currentTab]);

	useEffect(() => {
		replaceSearch((params) => {
			params.set("senders", JSON.stringify(selectedSenders));
		});
	}, [selectedSenders, location.search, navigate]);

	// When the URL carried no senders param, the initial state was built before the
	// sending users loaded — re-seed the default ("Me" when possible) exactly once
	// they arrive, keeping the same reference when nothing changed so the fetch
	// effect downstream is not re-triggered.
	const initialSenderParamRef = useRef(new URLSearchParams(location.search).get("senders"));

	useEffect(() => {
		if (initialSenderParamRef.current || !sendingUsersLoaded) {
			return;
		}

		const seed = buildDefaultSenderItem(user, doesSendingUsersHaveMe);

		setSelectedSenders((prev) => {
			if (prev.length === 1 && prev[0].id === seed.id && prev[0].title === seed.title) {
				return prev;
			}

			return [seed];
		});
	}, [sendingUsersLoaded, doesSendingUsersHaveMe]);

	const getCampaignIdsFromUrl = (): string[] => {
		const params = new URLSearchParams(location.search);
		const raw = params.get("campaignIds");

		try {
			const parsed = raw ? (JSON.parse(raw) as unknown) : [];

			return Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === "string") : [];
		} catch {
			return [];
		}
	};

	const [selectedCampaignIds, setSelectedCampaignIds] = useState<string[]>(getCampaignIdsFromUrl());

	useEffect(() => {
		replaceSearch((params) => {
			if (selectedCampaignIds.length > 0) {
				params.set("campaignIds", JSON.stringify(selectedCampaignIds));
			} else {
				params.delete("campaignIds");
			}
		});
	}, [selectedCampaignIds]);

	const getSourceFromUrl = (): OutboxSource => {
		const value = new URLSearchParams(location.search).get("outboxSource");

		return value === "chat" || value === "campaign" ? value : "all";
	};

	const [outboxSource, setOutboxSource] = useState<OutboxSource>(getSourceFromUrl());

	useEffect(() => {
		replaceSearch((params) => {
			if (outboxSource !== "all") {
				params.set("outboxSource", outboxSource);
			} else {
				params.delete("outboxSource");
			}
		});
	}, [outboxSource]);

	const getFilterFromUrl = (): IFilterWithValues => {
		const params = new URLSearchParams(location.search);

		return JSON.parse(params.get("filter") || "{}") as IFilterWithValues;
	};

	const [filter, setFilter] = useState<IFilterWithValues | undefined>(getFilterFromUrl());

	useEffect(() => {
		replaceSearch((params) => {
			if (filter) {
				params.set("filter", JSON.stringify(filter));
			} else {
				params.delete("filter");
			}
		});
	}, [filter, location.search, navigate]);

	useEffect(() => {
		replaceSearch((params) => {
			if (selectedLists.length > 0) {
				params.set("listIds", JSON.stringify(selectedLists));
			} else {
				params.delete("listIds");
			}
		});
	}, [selectedLists, navigate]);

	// Seed the text search from the URL on mount so deep-links (e.g. the Contacts lead
	// placement panel) can land the user on a name-filtered Outbox.
	useEffect(() => {
		const urlSearchValue = new URLSearchParams(location.search).get("searchValue") || "";

		if (urlSearchValue) {
			setSearchValue(urlSearchValue);
		}
	}, []);

	// Persist the text search to the URL so it survives reloads and is shareable.
	useEffect(() => {
		replaceSearch((params) => {
			if (searchValue) {
				params.set("searchValue", searchValue);
			} else {
				params.delete("searchValue");
			}
		});
	}, [searchValue, location.search, navigate]);

	return {
		currentTab,
		setCurrentTab,
		statusTab,
		setStatusTab,
		allInclude,
		toggleInclude,
		selectedLists,
		setSelectedLists,
		selectedSenders,
		setSelectedSenders,
		selectedCampaignIds,
		setSelectedCampaignIds,
		outboxSource,
		setOutboxSource,
		filter,
		setFilter,
	};
};
